import get from "lodash/get";
import {
    fetchInfo,
    createOrder
} from "@/api/modules/products/dsbstandardtickets";
import { makeUtcDate } from "@/utilities/time";

const InfoModel = function(info) {
    this.identifier = info.productTypeIdentifier;
    // Javascript will interpret the result as text
    this.allowedPassengerTypeIds = info.allowedPassengerTypeIds.map(id =>
        parseInt(id)
    );
    this.maximumNumberOfPassengersPerItem =
        info.maximumNumberOfPassengersPerItem;
    this.maximumStartValidityDaysFromNow = info.maximumStartValidityDaysFromNow;
};

export const state = {
    info: null
};

export const mutations = {
    updateInfo: (state, payload) => {
        state.info = new InfoModel(payload);
    }
};

export const getters = {
    isEnabled: state => Boolean(state.info)
};

export const actions = {
    fetchInfo: context => {
        return fetchInfo(true)
            .then(response => {
                if (!response.ok) throw new Error(response.responseBody);
                return response.responseBody;
            })
            .then(json => {
                context.commit("updateInfo", json);
                context.commit(
                    "system/addStopProductType",
                    {
                        productType: context.state.info.identifier
                    },
                    { root: true }
                );
            });
    },
    prepareOrder: (context, payload) => {
        const {
            allowedPassengerTypeIds,
            maximumStartValidityDaysFromNow,
            maximumNumberOfPassengersPerItem
        } = context.state.info;
        const serverTime = makeUtcDate(
            context.rootGetters.getServerDateAsMoment
        );
        const periodStart = makeUtcDate(serverTime)
            .add(-1, "days")
            .endOf("day");
        const periodEnd = makeUtcDate(serverTime)
            .add(maximumStartValidityDaysFromNow, "days")
            .startOf("day")
            .add(-1, "second");

        const {
            trip,
            serviceLevel,
            variantWithSeating,
            variantWithoutSeating,
            seatQuantity,
            seatType,
            calculatedPriceOre
        } = payload;

        if (trip) {
            if (!variantWithoutSeating && !variantWithSeating)
                throw new Error(
                    "cannot prepare dsb standard ticket order without product variants"
                );
            if (!calculatedPriceOre)
                throw new Error(
                    "cannot prepare dsb standard ticket order without calculatedPriceOre"
                );

            context.commit(
                "order/setDetails",
                {
                    trip,
                    serviceLevel,
                    variantWithoutSeatingId: get(
                        variantWithoutSeating,
                        "productVariantId",
                        null
                    ),
                    variantWithSeatingId: get(
                        variantWithSeating,
                        "productVariantId",
                        null
                    ),
                    calculatedPriceOre,
                    seatTypeId: get(seatType, "id", null),
                    seatProductId: get(seatType, "productId", null),
                    seatQuantity
                },
                { root: true }
            );
        }

        context.commit(
            "passenger/updatePassengers",
            {
                passengerTypeIds: allowedPassengerTypeIds,
                maximumPassengers: maximumNumberOfPassengersPerItem
            },
            { root: true }
        );

        return context.dispatch(
            "date/prepareSelectableDates",
            { periodStart, periodEnd },
            { root: true }
        );
    },
    createOrder: context => {
        const passengers = context.rootGetters["passenger/getPassengers"].map(
            passenger => {
                return {
                    passengerTypeId: passenger.passengerType.id,
                    passengerTypeCount: passenger.count
                };
            }
        );

        const preparedDetails = context.rootGetters["order/getDetails"];

        const {
            trip,
            variantWithSeatingId,
            variantWithoutSeatingId,
            seatTypeId,
            seatProductId,
            seatQuantity
        } = preparedDetails;

        if (!trip)
            throw new Error(
                "cannot create dsb standard ticket order without a trip"
            );

        return context
            .dispatch(
                "date/validateTripDepartureInstant",
                trip.departureInstant,
                { root: true }
            )
            .then(
                () => {
                    let productVariantId = variantWithoutSeatingId;
                    let seating = null;
                    if (seatTypeId && seatProductId && seatQuantity) {
                        seating = {
                            seatTypeId,
                            seatProductVariantId: seatProductId,
                            amountOfSeatsWanted: seatQuantity
                        };
                        if (variantWithSeatingId) {
                            productVariantId = variantWithSeatingId;
                        }
                    }

                    return createOrder(productVariantId, passengers, seating)
                        .then(response => {
                            if (response.ok) {
                                return response.responseBody;
                            } else if (
                                response.status === 422 &&
                                response.responseBody.error ===
                                    "TicketNotSellable"
                            ) {
                                throw new Error("TicketNotSellable");
                            } else {
                                throw new Error(JSON.stringify(response));
                            }
                        })
                        .then(({ orderId, priceOre, ...details }) => {
                            context.commit("order/setOrderId", orderId, {
                                root: true
                            });
                            context.commit("order/setPriceOre", priceOre, {
                                root: true
                            });
                            context.commit(
                                "order/setDetails",
                                {
                                    ...preparedDetails,
                                    ...details
                                },
                                { root: true }
                            );
                        });
                },
                () => {
                    throw new Error("InvalidTripDepartureInstant");
                }
            );
    }
};
